import {
  all,
  call,
  put,
  select,
  takeLatest,
  takeLeading,
} from 'redux-saga/effects';

import { del, get, post } from '../../../utils/request';
import makeSelectMain from '../../Main/selectors';
import makeSelectMobile from '../../Mobile/selectors';
import { setExistingInfo, updateFormErrors } from '../Sources/actions';
import {
  cancelOtpFailure,
  cancelOtpSuccess,
  changeStep,
  createOtpFailure,
  createOtpSuccess,
  createOtpValidationFailure,
  fetchPaymentHomeFailure,
  fetchPaymentHomeSuccess,
  setIsModalOpen,
} from './actions';
import {
  CANCEL_OTP,
  CREATE_OTP,
  DEFAULT_CREATE_OTP_ERRORS,
  FETCH_PAYMENT_HOME,
  FULL_ERROR_REASON_CODES,
  REASON_CODE_DICTIONARY,
} from './constants';
import { checkFullErrorReasonCodes, parseReasonCodes } from './helpers';

export function* cancelOtpSaga(
  {
    baseUrl,
    navigationHandler,
    navigationReplaceHandler,
  },
  { payload: { navigationType } },
) {
  const isMobile = yield select(makeSelectMobile('isMobile'));
  const isNativeMobile = yield select(makeSelectMobile('isNativeMobile'));
  try {
    const loanNumber = yield select(makeSelectMain('selectedLoan'));
    const {
      cancelConfirmationNumber,
      error,
    } = yield call(del, `${baseUrl}/payments/otp/${loanNumber}`);
    if (error) {
      yield put(cancelOtpFailure());
    } else {
      yield put(cancelOtpSuccess(cancelConfirmationNumber));
    }
    if (isMobile) {
      /*
        TODO: figure out if/how we can (or care to) properly replace whole Otp flow on mobile web
        (see TODO comment in createOtpSaga about 'replace' navigation.) For now on web, just push,
        don't replace.
      */
      const properNavigationHandler = navigationType === 'replace' && isNativeMobile ?
        navigationReplaceHandler :
        navigationHandler;
      properNavigationHandler('CancelConfirmation');
    } else {
      navigationHandler('/payments/otp');
      yield put(changeStep(5));
    }
    // updating modal state after navigation prevents "Can't perform a React
    // state update on an unmounted component." console error
    yield put(setIsModalOpen({ key: 'cancel', value: false }));
  } catch (error) {
    yield put(cancelOtpFailure());
    yield put(setIsModalOpen({ key: 'cancel', value: false }));
    if (isMobile) {
      const properNavigationHandler = navigationType === 'replace' && isNativeMobile ?
        navigationReplaceHandler :
        navigationHandler;
      properNavigationHandler('CancelConfirmation');
    } else {
      navigationHandler('/payments/otp');
      yield put(changeStep(5));
    }
  }
}

export function* createOtpSaga(
  {
    baseUrl,
    navigationHandler,
    navigationReplaceHandler,
  },
  { payload },
) {
  const isMobile = yield select(makeSelectMobile('isMobile'));
  const isNativeMobile = yield select(makeSelectMobile('isNativeMobile'));
  try {
    const { createOtpRequestBody, loanNumber } = payload;
    const { error, ...response } =
      yield call(post, `${baseUrl}/payments/otp/${loanNumber}`, createOtpRequestBody);
    if (error) {
      const { reasons } = error;
      if (
        reasons[0].startsWith('15004') &&
        !checkFullErrorReasonCodes(reasons, FULL_ERROR_REASON_CODES)
      ) {
        const { otp, sources } = parseReasonCodes(reasons, DEFAULT_CREATE_OTP_ERRORS);
        const { screen } = REASON_CODE_DICTIONARY[reasons[0]];
        yield put(createOtpValidationFailure({ ...otp, screen }));
        yield put(updateFormErrors(sources));
        if (!screen) throw new Error('Invalid Screen');
        if (isMobile) {
          navigationHandler('SchedulePayment');
          if (screen !== 'SchedulePayment') {
            navigationHandler(screen);
          }
          return;
        }
        yield put(changeStep(1));
        return;
      }
      yield put(createOtpFailure());
    } else {
      yield put(createOtpSuccess(response));
    }
    // Handle navigation to the OTP Confirmation page
    if (isMobile) {
      /*
        TODO: figure out if/how we can (or care to) prevent user from hitting browser Back button
        to get back into OTP flow from the Confirmation page. On native, navigationReplaceHandler
        is able to replace the whole stack of pre-confirmation OTP screens with the singular
        OtpConfirmationScreen. On web, it only replaces the last pre-confirmation OTP screen, not
        the whole stack. So, for now, might as well just use the normal navigationHandler (push)
        for mobile web.
      */
      const properNavigationHandler = isNativeMobile ? navigationReplaceHandler : navigationHandler;
      properNavigationHandler('OtpConfirmation');
    } else {
      yield put(changeStep(4));
    }
  } catch {
    yield put(createOtpFailure());
    // Handle navigation to the OTP Confirmation page
    if (isMobile) {
      const properNavigationHandler = isNativeMobile ? navigationReplaceHandler : navigationHandler;
      properNavigationHandler('OtpConfirmation');
    } else {
      yield put(changeStep(4));
    }
  }
}

/*
  TODO: refactor in name to just be for OTP related info (and maybe autopay?), and refactor in
  logic to include more data related to OTP (and autopay?). A separate saga handles payment home
  history info.
*/
export function* paymentHomeSaga({ baseUrl }, { payload: { loanNumber } }) {
  try {
    const { error, ...response } = yield call(get, `${baseUrl}/payments/home/${loanNumber}`);
    if (error) {
      const [reason] = error.reasons;
      /**
       * 15001 error code check ensures an ineligible loan renders
       * Payment Home with a loan is ineligible message
       */
      if (reason.startsWith('15001')) {
        yield put(fetchPaymentHomeSuccess(response));
      } else {
        yield put(fetchPaymentHomeFailure());
      }
    } else {
      const { canUseExistingAccountInformation, existingAccountInformation } = response;
      if (canUseExistingAccountInformation) {
        yield put(setExistingInfo(existingAccountInformation));
      }
      yield put(fetchPaymentHomeSuccess(response));
    }
  } catch {
    yield put(fetchPaymentHomeFailure());
  }
}

export default (options) => function* watcherSaga() {
  yield all([
    takeLeading(CREATE_OTP, createOtpSaga, options),
    takeLatest(FETCH_PAYMENT_HOME, paymentHomeSaga, options),
    takeLeading(CANCEL_OTP, cancelOtpSaga, options),
  ]);
};
